import { defineStore } from 'pinia'
import { useDeviceStore } from '@/stores/device/store'
import { useNetworkStore } from '@/stores/network/store'
import * as Api from './api'
import {
  callWithMeta,
  createMeta,
  getMetaInitial,
  waitFor,
  type Meta,
} from '@/utils/store'
import { useExtensionStore } from '@/stores/extension/store'
import { useExtensionsStore } from '@/stores/extensions/store'
import * as ApiDevice from '@/stores/device/api'
import { transformOutgoingDevice } from '@/utils/dataTransformers'
import { useOfficeDesksStore } from '@/stores/officeDesks/store'
import { useOfficeDeskStore } from '@/stores/officeDesk/store'
import type { ColorName } from '@/utils/colors'

type State = {
  devices: ApiDevice.Device[]
  meta: Meta<{
    devices: undefined
    load: undefined
    update: undefined
    search: undefined
    delete: undefined
    changeColor: undefined
  }>
}

const getDefaultState = (): State => ({
  devices: [],
  meta: createMeta([
    'devices',
    'load',
    'update',
    'search',
    'delete',
    'changeColor',
  ]),
})

export const useDevicesStore = defineStore('devices', {
  state: getDefaultState,

  getters: {
    find: (state) => (id: number) =>
      state.devices.find((device) => device.id === id),
    list: (state) => state.devices,
  },

  actions: {
    RESET() {
      this.devices = []
      this.meta.devices = getMetaInitial()
    },

    SET_GROUP_TAGS(payload: any) {
      this.devices.forEach((device) => {
        const sameGroups: any[] = []

        let deleteGroup = false

        device.groups?.forEach((group) => {
          if (group.label === payload.label && group.color === payload.color) {
            sameGroups.push(group.id)
          }

          if (
            group.group_tag_id ===
            (payload.old_id ? payload.old_id : payload.id)
          ) {
            Object.assign(group, {
              label: payload.label,
              color: payload.color,
              group_tag_id: payload.id,
            })
            deleteGroup = true
          }
        })

        if (deleteGroup) {
          device.groups = device.groups?.filter(
            (group) => !sameGroups.includes(group.id)
          )
        }
      })
    },

    DELETE_GROUP_TAGS(payload: any) {
      this.devices.forEach((device) => {
        device.groups = device.groups?.filter(
          (group) => group.group_tag_id != payload
        )
      })
    },

    DELETE_GROUP_TAG(payload: any) {
      this.devices.forEach((device) => {
        device.groups = device.groups?.filter((group) => group.id != payload.id)
      })
    },

    ADD_GROUP_TAG(payload: any) {
      this.devices.forEach((device) => {
        if (device.id === payload.elementId) {
          delete payload.elementId

          const exists = device.groups.filter(
            (group) => group.group_tag_id === payload.group_tag_id
          )

          if (!exists.length) {
            device.groups.push(payload)
          }
        }
      })
    },

    async load({ useCache = true } = {}) {
      const hasCached = ['pending', 'success'].includes(
        this.meta.devices.status
      )

      if (useCache && hasCached) {
        await waitFor(() => this.meta.devices.status !== 'pending')

        return
      }

      await callWithMeta({
        store: this,
        metaFields: { load: undefined, devices: undefined },
        apiCall: async () => await Api.loadDevices(),
        onSuccess: ({ result, isLatest }) => {
          if (isLatest('devices')) {
            this.devices = result.payload
          }
        },
        onError: ({ result, isLatest }) => {
          if (isLatest('load')) {
            useNetworkStore().HANDLE_LOADING_ERROR(result.payload)
          }
        },
      })
    },

    async changeColor({
      color,
      itemId,
    }: {
      color: ColorName | null
      itemId: number
    }) {
      // Show color change before waiting for API call

      const deviceIndex = this.devices.findIndex(
        (device) => device.id === itemId
      )

      if (deviceIndex >= 0) {
        this.devices[deviceIndex].color = color
      }

      // Send color change to the BE

      const { deviceData } = transformOutgoingDevice({ color })

      const { result } = await callWithMeta({
        store: this,
        metaFields: { changeColor: undefined },
        apiCall: async () => await ApiDevice.updateDevice(itemId, deviceData),
        onSuccess: ({ result, isLatest }) => {
          if (isLatest('changeColor')) {
            const updatedDevice = result.payload

            const deviceIndex = this.devices.findIndex(
              (device) => device.id === updatedDevice.id
            )

            this.devices[deviceIndex] = updatedDevice
          }
        },
      })

      if (result.status === 'error') {
        throw result.payload
      }
    },

    async update({ data }: any) {
      const { result } = await callWithMeta({
        store: this,
        metaFields: { update: undefined, devices: undefined },
        apiCall: async () => await Api.updateDevices(data),
        onSuccess: ({ isLatest }) => {
          if (isLatest('update')) {
            useExtensionsStore().load({ useCache: false })

            useExtensionStore().reload()

            this.load({ useCache: false })

            useDeviceStore().reload()

            setTimeout(async () => {
              await useOfficeDesksStore().load({ useCache: false })

              useOfficeDeskStore().reload()
            })
          }
        },
      })

      if (result.status === 'success') {
        return result.payload
      }

      throw result.payload
    },

    async search(deviceIds: number[]) {
      const { result } = await callWithMeta({
        store: this,
        metaFields: { search: undefined },
        apiCall: async () => await Api.searchDevices(deviceIds),
      })

      if (result.status === 'success') {
        return result.payload
      }

      throw result.payload
    },

    async delete(deviceIds: number[]) {
      const { result } = await callWithMeta({
        store: this,
        metaFields: { delete: undefined, devices: undefined },
        apiCall: async () => await Api.deleteDevices(deviceIds),
        onSuccess: ({ isLatest }) => {
          if (isLatest('devices')) {
            this.devices = this.devices.filter(
              (device) => !deviceIds.includes(device.id)
            )

            if (
              deviceIds.includes(
                Number(useDeviceStore().meta.deviceData.params?.id)
              )
            ) {
              useDeviceStore().RESET()
            }
          }

          if (isLatest('delete')) {
            useExtensionsStore().load({ useCache: false })

            useExtensionStore().reload()

            setTimeout(async () => {
              await useOfficeDesksStore().load({ useCache: false })

              useOfficeDeskStore().reload()
            })
          }
        },
      })

      if (result.status === 'success') {
        return result.payload
      }

      throw result.payload
    },
  },
})
